// Runtime access to the ShrinkDaily inbound-contract that AR renders
// reciprocal links against. The contract is refreshed at build time by
// scripts/fetch-shrinkdaily-inbound-contract.mjs and committed under
// src/data/__generated__/shrinkdailyInbound.json so builds work even when
// the ShrinkDaily fetch is unreachable at build time.
//
// The public surface here is deliberately small: one function that returns
// every entry whose sourceUrl matches the current page. Rendering happens in
// src/components/InboundReciprocalLink.tsx.

import raw from "@/data/__generated__/shrinkdailyInbound.json";

export type InboundPlacement =
  | "inline-first-mention"
  | "see-also"
  | "related-reading"
  | "footer-crosslink";

export interface InboundEntry {
  id: string;
  sourceUrl: string;
  targetUrl: string;
  anchor: string;
  placement: InboundPlacement;
  utm: string;
  surroundingProse: string;
}

interface InboundContract {
  publisher: string;
  consumer: string;
  spec_version: string | null;
  updated: string | null;
  voice_constraints: Record<string, unknown>;
  entries: InboundEntry[];
}

const CONTRACT = raw as unknown as InboundContract;

const AR_ORIGIN = "https://anxietyresource.org";

// Normalize a source URL to a canonical AR path. The contract publishes
// absolute URLs (`https://anxietyresource.org/`, `.../library`), but the
// runtime callers pass a route-relative path (`/`, `/library`). This
// converts the contract's sourceUrl to a comparable path form.
function contractPath(url: string): string {
  try {
    const u = new URL(url);
    if (u.origin !== AR_ORIGIN) return url;
    const p = u.pathname.replace(/\/+$/, "");
    return p === "" ? "/" : p;
  } catch {
    return url;
  }
}

function currentPath(pathname: string): string {
  const p = pathname.replace(/\/+$/, "");
  return p === "" ? "/" : p;
}

/**
 * Build the target URL with the contract's declared UTM string appended.
 * The contract publishes a `utm` starting with `?`, so we concatenate
 * directly. If the targetUrl already carries query params we still ship
 * the contract's UTM string (contracts are the source of truth).
 */
export function entryHref(entry: InboundEntry): string {
  const utm = entry.utm ?? "";
  if (!utm) return entry.targetUrl;
  // Handle contract UTMs that start with `?` or `&`.
  const glue = entry.targetUrl.includes("?")
    ? utm.startsWith("?")
      ? `&${utm.slice(1)}`
      : utm
    : utm.startsWith("?")
      ? utm
      : `?${utm}`;
  return `${entry.targetUrl}${glue}`;
}

/**
 * All entries whose sourceUrl matches the given AR route path. Callers
 * pass the router pathname (for example `/library`). Returns an empty
 * array when no entry matches, which the caller renders as null.
 */
export function entriesForPath(pathname: string): InboundEntry[] {
  const p = currentPath(pathname);
  return CONTRACT.entries.filter((e) => contractPath(e.sourceUrl) === p);
}

/**
 * Every entry with a given placement, useful for structural rendering
 * (a Footer that iterates every `footer-crosslink` entry matching the
 * current route). Callers combine this with `entriesForPath` when they
 * need both filters.
 */
export function entriesForPlacementOnPath(
  pathname: string,
  placement: InboundPlacement,
): InboundEntry[] {
  return entriesForPath(pathname).filter((e) => e.placement === placement);
}

export function contractSummary(): {
  publisher: string;
  spec_version: string | null;
  updated: string | null;
  count: number;
} {
  return {
    publisher: CONTRACT.publisher,
    spec_version: CONTRACT.spec_version,
    updated: CONTRACT.updated,
    count: CONTRACT.entries.length,
  };
}
